// Browser-side content assembly (the Node harness has its own fs-based loader).
// Vite inlines the JSON at build time; buildContentPack validates on load.
// P1: scenario-aware — each scenario card names its market directory, rival
// roster, and tree files (mirrors loadContent.ts's loader hints). Market lists
// mirror the harness directory scan: alphabetical by filename.

import nodesJson from "../../content/nodes.json";
import cableModemJson from "../../content/markets/cable-modem.json";
import dslJson from "../../content/markets/dsl.json";
import phyJson from "../../content/markets/ethernet-phy.json";
import gameConsoleJson from "../../content/markets/game-console.json";
import hddJson from "../../content/markets/hdd-read-channel.json";
import mobileAppsJson from "../../content/markets/mobile-apps.json";
import modemsJson from "../../content/markets/modems.json";
import chipsetsJson from "../../content/markets/pc-chipsets.json";
import graphicsJson from "../../content/markets/pc-graphics.json";
import setTopJson from "../../content/markets/set-top-box.json";
import storageNetJson from "../../content/markets/storage-networking.json";
import telecomJson from "../../content/markets/telecom-sonet.json";
import wifiJson from "../../content/markets/wifi.json";
import basebandJson from "../../content/markets/wireless-baseband.json";
import gfxArcadeJson from "../../content/markets-3dgfx/gfx-arcade.json";
import gfxOemJson from "../../content/markets-3dgfx/gfx-oem.json";
import gfxRetailJson from "../../content/markets-3dgfx/gfx-retail.json";
import economyJson from "../../content/economy.json";
import foundersScenarioJson from "../../content/scenarios/founders-1993.json";
import gfxScenarioJson from "../../content/scenarios/3dgfx-1993.json";
import eventsJson from "../../content/events.json";
import rivalsJson from "../../content/rivals.json";
import rivals3dgfxJson from "../../content/rivals-3dgfx.json";
import tree3dgfxJson from "../../content/trees/tree-3dgfx.json";
import ipJson from "../../content/ip.json";
import asksJson from "../../content/asks.json";
import type {
  AskDef,
  ContentPack,
  EconomyDef,
  HistoryEventDef,
  IpDef,
  MarketDef,
  NodeDef,
  RivalsContent,
  ScenarioDef,
  TechTreeDef,
} from "../engine/types.js";
import { buildContentPack } from "../engine/content.js";
import { CONTENT_VERSION } from "../contentVersion.js";
import { LEGACY_SCENARIO_ID } from "./session.js";

/** Card copy for scenario select (app-side: ScenarioDef carries no display text). */
export interface ScenarioMeta {
  id: string;
  title: string;
  dateLabel: string;
  marketLabel: string;
  blurb: string;
  tags: string[];
}

export const SCENARIOS: ScenarioMeta[] = [
  {
    id: "3dgfx-1993",
    title: "3D GRAPHICS — THE GARAGE",
    dateLabel: "JAN 1993 → DEC 2008",
    marketLabel: "PC 3D graphics: OEM · retail · arcade",
    blurb:
      "You are three people in a booth at Denny's with a thesis about polygons. Research a founding bet, " +
      "build the demo, and pitch your way to a cap table — then survive the gold rush, the API wars, and the " +
      "extinction of 1999. Nobody hands you a company in this one.",
    tags: ["TECH TREE", "GARAGE START", "STORY CAMPAIGN"],
  },
  {
    id: "founders-1993",
    title: "FABLESS 1993 — CLASSIC",
    dateLabel: "JAN 1993 → DEC 2008",
    marketLabel: "14 markets, open field",
    blurb:
      "The original campaign: a funded fabless startup with $3.8M of seed money and ten founders. " +
      "Pick your markets, ride the design-win treadmill, and take it public before the cycle turns.",
    tags: ["OPEN SANDBOX", "INCORPORATED START"],
  },
];

const LEGACY_MARKETS = [
  cableModemJson,
  dslJson,
  phyJson,
  gameConsoleJson,
  hddJson,
  mobileAppsJson,
  modemsJson,
  chipsetsJson,
  graphicsJson,
  setTopJson,
  storageNetJson,
  telecomJson,
  wifiJson,
  basebandJson,
] as unknown as MarketDef[];

// markets-3dgfx/, alphabetical by filename (matches the harness scan)
const GFX_MARKETS = [gfxArcadeJson, gfxOemJson, gfxRetailJson] as unknown as MarketDef[];

const packs = new Map<string, ContentPack>();

export function loadBrowserContent(scenarioId: string = LEGACY_SCENARIO_ID): ContentPack {
  const cached = packs.get(scenarioId);
  if (cached) return cached;
  const is3dgfx = scenarioId === "3dgfx-1993";
  if (!is3dgfx && scenarioId !== LEGACY_SCENARIO_ID) throw new Error(`unknown scenario ${scenarioId}`);
  const pack = buildContentPack({
    version: CONTENT_VERSION,
    nodes: nodesJson as unknown as NodeDef[],
    markets: is3dgfx ? GFX_MARKETS : LEGACY_MARKETS,
    economy: economyJson as unknown as EconomyDef,
    scenario: (is3dgfx ? gfxScenarioJson : foundersScenarioJson) as unknown as ScenarioDef,
    events: eventsJson as unknown as HistoryEventDef[],
    rivals: (is3dgfx ? rivals3dgfxJson : rivalsJson) as unknown as RivalsContent,
    asks: asksJson as unknown as AskDef[],
    ip: ipJson as unknown as IpDef[],
    trees: is3dgfx ? [tree3dgfxJson as unknown as TechTreeDef] : [],
  });
  packs.set(scenarioId, pack);
  return pack;
}
